/**
 * What a computer asking to join reported, in the words and layout of the Add machine dialog's
 * approve card (features/machines/AddMachineDialog.tsx): System, Account, Key fingerprint. The
 * approval page (/pair/:id) and the dialog show it the same way, so the two read as one product.
 */
import { OS_LABEL } from '@orgus/protocol';
import type { PairMachine, PairRequestView } from '@orgus/protocol';
import { cn } from '@orgus/ui';

/** "Windows 11 Pro 24H2, x64": the version the computer reported (else its OS), and the architecture. */
export function pairSystemLabel(m: PairMachine): string {
  const os = OS_LABEL[m.os === 'darwin' ? 'macos' : m.os];
  // "11 23H2" reads as "Windows 11 23H2"; a version that names its system stays as it is.
  const version = !m.osVersion ? os : m.osVersion.toLowerCase().includes(os.toLowerCase()) ? m.osVersion : `${os} ${m.osVersion}`;
  return `${version}, ${m.arch === 'amd64' ? 'x64' : 'Arm64'}`;
}

/** The sentence for a computer on another network, the same on the page and in the dialog. */
export const PAIR_DIFFERENT_NETWORK = 'This computer is on a different network from you. Approve it only if it is yours.';

export function PairRequestFacts({ request, fingerprint = true, className }: { request: Pick<PairRequestView, 'machine' | 'fingerprint'>; fingerprint?: boolean; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-2 text-caption sm:grid-cols-2', className)}>
      <div className="flex min-w-0 flex-col">
        <dt className="text-fg-subtle">System</dt>
        <dd className="truncate text-fg">{pairSystemLabel(request.machine)}</dd>
      </div>
      <div className="flex min-w-0 flex-col">
        <dt className="text-fg-subtle">Account</dt>
        <dd className="truncate text-fg">{request.machine.user}</dd>
      </div>
      {fingerprint && (
        <div className="flex min-w-0 flex-col sm:col-span-2">
          <dt className="text-fg-subtle">Key fingerprint</dt>
          <dd className="font-mono text-fg">{request.fingerprint}</dd>
        </div>
      )}
    </dl>
  );
}
